import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, InjectionToken, inject } from '@angular/core';

import { LoginRedirectService } from '@shared/auth/login-redirect.service';

import { teachersMe } from '@api';

import { ENVIRONMENT } from '@environments/environment';

import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { StatusCodes } from 'http-status-codes';

import { Observable, Subject, debounceTime, filter, map } from 'rxjs';

const CHANGED_SIGNAL = 'Changed';
const RECONNECT_DELAYS_MS = [0, 2000, 5000, 10_000];
const MAX_RECONNECT_DELAY_MS = 30_000;
const REFRESH_DEBOUNCE_MS = 300;
const CHANGE_TOPICS = ['activities', 'projects', 'calendar'] as const;

export type ChangeTopic = (typeof CHANGE_TOPICS)[number];

export const CHANGES_HUB_CONNECTION = new InjectionToken<HubConnection>('CHANGES_HUB_CONNECTION', {
  providedIn: 'root',
  factory: (): HubConnection =>
    new HubConnectionBuilder()
      .withUrl(`${ENVIRONMENT.API_URL}/hubs/changes`, {
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
        withCredentials: true,
      })
      .configureLogging(LogLevel.Warning)
      .build(),
});

@Injectable({ providedIn: 'root' })
export class ChangeEventsService {
  readonly #document = inject(DOCUMENT);
  readonly #loginRedirect = inject(LoginRedirectService);

  readonly #changedTopics = new Subject<readonly ChangeTopic[]>();
  readonly #connection = this.#listenTo(inject(CHANGES_HUB_CONNECTION));
  #reconnectAttempt = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  #connecting = false;
  #stopped = false;
  readonly #onVisibilityChange = (): void => {
    if (this.#document.visibilityState !== 'visible' || this.#connection.state !== HubConnectionState.Disconnected) {
      return;
    }
    clearTimeout(this.#reconnectTimer);
    this.#reconnectAttempt = 0;
    void this.#connect();
  };

  constructor() {
    inject(DestroyRef).onDestroy(() => this.#stop());
  }

  refreshRequested(topic: ChangeTopic, ...more: readonly ChangeTopic[]): Observable<void> {
    const topics = new Set<ChangeTopic>([topic, ...more]);
    return this.#changedTopics.pipe(
      filter((changed) => changed.some((changedTopic) => topics.has(changedTopic))),
      map(() => undefined),
      debounceTime(REFRESH_DEBOUNCE_MS),
    );
  }

  start(): void {
    this.#stopped = false;
    this.#document.addEventListener('visibilitychange', this.#onVisibilityChange);
    void this.#connect();
  }

  #stop(): void {
    this.#stopped = true;
    clearTimeout(this.#reconnectTimer);
    this.#document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    void this.#connection.stop().catch(() => undefined);
  }

  #listenTo(connection: HubConnection): HubConnection {
    connection.on(CHANGED_SIGNAL, (topics: ChangeTopic[]) => this.#changedTopics.next(topics));
    connection.onclose(() => this.#scheduleReconnect());

    return connection;
  }

  async #connect(): Promise<void> {
    if (this.#stopped || this.#connecting || this.#connection.state !== HubConnectionState.Disconnected) {
      return;
    }
    this.#connecting = true;

    try {
      const session = await teachersMe({ throwOnError: false });
      if (session.response?.status === StatusCodes.UNAUTHORIZED) {
        this.#stopped = true;
        this.#loginRedirect.redirectToLoginApp();
        return;
      }
      if (session.error !== undefined) {
        this.#scheduleReconnect();
        return;
      }

      await this.#connection.start();
      this.#reconnectAttempt = 0;
      this.#changedTopics.next(CHANGE_TOPICS);
    } catch {
      this.#scheduleReconnect();
    } finally {
      this.#connecting = false;
    }
  }

  #scheduleReconnect(): void {
    if (this.#stopped) {
      return;
    }
    clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = setTimeout(() => void this.#connect(), this.#nextReconnectDelay());
  }

  #nextReconnectDelay(): number {
    return RECONNECT_DELAYS_MS[this.#reconnectAttempt++] ?? MAX_RECONNECT_DELAY_MS;
  }
}
